import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { BookOpen, Lock, Unlock, Coins, ArrowLeft, Loader2, Sparkles, Crown } from "lucide-react";
import Btn from "../components/Btn";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const EXAM_META = {
  CPNS: { color: "var(--su-primary)", label: "CPNS" },
  SNBT: { color: "var(--su-success)", label: "SNBT" },
};

export default function Modules() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [opening, setOpening] = useState(null);

  const load = () => api.get("/modules").then(({ data }) => setData(data)).catch(() => setData({ modules: [], token_cost: 1 }));
  useEffect(() => { load(); }, []);

  const open = async (m) => {
    if (data?.vip_required) {
      toast.error("Modul materi khusus VIP. Yuk upgrade ke VIP!");
      nav("/pricing");
      return;
    }
    setOpening(m.module_id);
    try {
      const { data: res } = await api.post(`/modules/${m.module_id}/open`);
      setActive(res.module);
      if (res.charged) { toast.success(`Modul terbuka! -${data.token_cost} token`); refresh?.(); load(); }
    } catch (e) { toast.error(apiErr(e)); } finally { setOpening(null); }
  };

  if (!data) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="w-10 h-10 animate-spin text-su-primary" /></div>;

  if (active) return (
    <div className="max-w-3xl mx-auto" data-testid="module-reader">
      <button onClick={() => setActive(null)} data-testid="module-back" className="flex items-center gap-2 font-heading font-bold text-su-muted hover:text-su-text mb-4"><ArrowLeft className="w-4 h-4" /> Kembali ke daftar modul</button>
      <div className="card-chunky p-6 sm:p-8">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] font-heading font-black uppercase px-2 py-0.5 rounded-full" style={{ color: EXAM_META[active.exam]?.color, background: `${EXAM_META[active.exam]?.color}22` }}>{active.exam} · {active.topic}</span>
        </div>
        <h1 className="font-heading font-black text-2xl sm:text-3xl mb-6">{active.title}</h1>
        <article className="module-prose">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{active.content}</ReactMarkdown>
        </article>
      </div>
    </div>
  );

  const byExam = {};
  data.modules.forEach((m) => { (byExam[m.exam] = byExam[m.exam] || []).push(m); });

  return (
    <div className="space-y-8" data-testid="modules-page">
      <div>
        <h1 className="font-heading font-black text-2xl sm:text-3xl flex items-center gap-2"><BookOpen className="w-7 h-7 text-su-primary" /> Modul Materi</h1>
        <p className="text-su-muted font-medium">Buka modul dengan <span className="inline-flex items-center gap-1 font-bold text-su-secondaryShadow"><Coins className="w-4 h-4" />{data.token_cost} token</span> (sekali buka, akses selamanya).</p>
      </div>

      {data.vip_required && (
        <div className="card-chunky p-5 flex flex-col sm:flex-row items-center gap-4 bg-su-secondary/5 border-su-secondary/40" data-testid="modules-vip-banner">
          <div className="w-12 h-12 rounded-2xl bg-su-secondary/20 grid place-items-center shrink-0"><Crown className="w-6 h-6 text-su-secondaryShadow" /></div>
          <div className="flex-1 text-center sm:text-left">
            <p className="font-heading font-black">Modul materi khusus member VIP 🔒</p>
            <p className="text-su-muted font-medium text-sm">Upgrade ke VIP untuk membuka semua modul CPNS & SNBT.</p>
          </div>
          <Link to="/pricing"><Btn variant="secondary" data-testid="modules-upgrade-btn"><Crown className="w-4 h-4" /> Upgrade VIP</Btn></Link>
        </div>
      )}

      {data.modules.length === 0 ? (
        <div className="card-chunky p-10 text-center" data-testid="modules-empty">
          <Sparkles className="w-10 h-10 text-su-muted mx-auto mb-3" />
          <p className="font-heading font-black text-lg">Belum ada modul</p>
          <p className="text-su-muted font-medium">Materi sedang disiapkan admin. Cek lagi nanti ya! 📚</p>
        </div>
      ) : (
        Object.keys(byExam).map((exam) => (
          <div key={exam}>
            <h2 className="font-heading font-black text-lg mb-3 flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: EXAM_META[exam]?.color }} /> {EXAM_META[exam]?.label || exam}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {byExam[exam].map((m) => (
                <button key={m.module_id} onClick={() => open(m)} disabled={opening === m.module_id} data-testid={`module-card-${m.module_id}`}
                  className="card-chunky p-5 text-left hover:-translate-y-1 transition-transform disabled:opacity-60">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-heading font-black uppercase px-2 py-0.5 rounded-full" style={{ color: EXAM_META[exam]?.color, background: `${EXAM_META[exam]?.color}22` }}>{m.topic}</span>
                    {m.unlocked
                      ? <span className="flex items-center gap-1 text-[11px] font-heading font-black text-su-success"><Unlock className="w-3.5 h-3.5" /> Terbuka</span>
                      : <span className="flex items-center gap-1 text-[11px] font-heading font-black text-su-secondaryShadow"><Coins className="w-3.5 h-3.5" />{data.token_cost}</span>}
                  </div>
                  <h3 className="font-heading font-black text-base mb-1">{m.title}</h3>
                  <p className="text-xs text-su-muted line-clamp-3">{m.summary}</p>
                  <div className="mt-3 flex items-center gap-1.5 text-sm font-heading font-bold text-su-primary">
                    {opening === m.module_id ? <><Loader2 className="w-4 h-4 animate-spin" /> Membuka...</> : m.unlocked ? <><BookOpen className="w-4 h-4" /> Baca Materi</> : <><Lock className="w-4 h-4" /> Buka Modul</>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
